import { subDays, format } from 'date-fns';
import { DEMO_ACCOUNT_IDS } from '@/constants';

// Demo account configurations
const DEMO_ACCOUNTS = {
  'c8661641-a2e9-41d2-9c9e-2f63e781af56': { // @rhode
    username: 'rhode',
    baseFollowers: 1100000,
    dailyGrowth: 1200, // ~8400/week (aggressive)
  },
  '7ed50c53-c969-4fa3-bd1d-c390c5f9a014': { // @emmachamberlain
    username: 'emmachamberlain',
    baseFollowers: 15800000,
    dailyGrowth: 5000, // ~35K/week
  },
  '320a4765-1df8-423f-925a-263538e2f059': { // @matildadjerf
    username: 'matildadjerf',
    baseFollowers: 2900000,
    dailyGrowth: 2000, // ~14K/week
  },
} as const;

export interface ChartDataPoint {
  date: string;
  followers: number;
  followings: number;
  gained: number;
  lost: number;
}

export const generateDemoChartData = (
  accountId: string,
  days: number = 90
): ChartDataPoint[] => {
  const config = DEMO_ACCOUNTS[accountId as keyof typeof DEMO_ACCOUNTS];
  if (!config) return [];

  const { baseFollowers, dailyGrowth } = config;

  return Array.from({ length: days }, (_, i) => {
    const date = subDays(new Date(), days - i - 1);
    const randomVariation = 0.8 + Math.random() * 0.4; // ±20%
    const dayGrowth = Math.floor(dailyGrowth * randomVariation);
    const followers = Math.floor(baseFollowers + (i * dailyGrowth * randomVariation));
    const lost = Math.floor(dayGrowth * 0.1 * Math.random()); // ~10% churn

    return {
      date: format(date, 'yyyy-MM-dd'),
      followers,
      followings: Math.floor(followers * 0.001), // ~0.1% ratio
      gained: dayGrowth + lost, // Gross gain
      lost,
    };
  });
};

// Demo analytics data for @rhode (beauty/skincare brand)
export const generateDemoAnalytics = (accountId: string) => {
  const config = DEMO_ACCOUNTS[accountId as keyof typeof DEMO_ACCOUNTS];
  if (!config) return null;

  // Analytics for @rhode (Hailey Bieber's beauty brand - 7.2M followers)
  if (accountId === 'c8661641-a2e9-41d2-9c9e-2f63e781af56') {
    return {
      top_5_countries: {
        'United States': 0.48,
        'United Kingdom': 0.13,
        'Canada': 0.09,
        'Australia': 0.07,
        'Germany': 0.06,
      },
      top_5_traffic_sources: {
        'Direct Profile Visits': 0.35,
        'Instagram Explore': 0.22,
        'Hashtags': 0.20,
        'Stories & Reels': 0.14,
        'External Links': 0.09,
      },
      top_5_interests: {
        'Beauty': 0.42,
        'Fashion': 0.20,
        'Health & Wellness': 0.15,
        'Lifestyle': 0.13,
        'Travel': 0.10,
      },
    };
  }

  // Analytics for @emmachamberlain (Gen-Z lifestyle influencer - 16M followers)
  if (accountId === '7ed50c53-c969-4fa3-bd1d-c390c5f9a014') {
    return {
      top_5_countries: {
        'United States': 0.54,
        'United Kingdom': 0.14,
        'Canada': 0.08,
        'Australia': 0.08,
        'Brazil': 0.05,
      },
      top_5_traffic_sources: {
        'Instagram Explore': 0.32,
        'Hashtags': 0.25,
        'Direct Profile Visits': 0.18,
        'Stories & Reels': 0.17,
        'Collaborations': 0.08,
      },
      top_5_interests: {
        'Lifestyle': 0.38,
        'Fashion': 0.22,
        'Food & Coffee': 0.16,
        'Beauty': 0.12,
        'Travel': 0.12,
      },
    };
  }

  // Analytics for @matildadjerf (Swedish fashion influencer - 3.3M followers)
  if (accountId === '320a4765-1df8-423f-925a-263538e2f059') {
    return {
      top_5_countries: {
        'Sweden': 0.32,
        'United States': 0.26,
        'United Kingdom': 0.13,
        'Norway': 0.10,
        'Denmark': 0.07,
      },
      top_5_traffic_sources: {
        'Hashtags': 0.32,
        'Direct Profile Visits': 0.26,
        'Stories & Reels': 0.19,
        'Instagram Explore': 0.15,
        'External Links': 0.08,
      },
      top_5_interests: {
        'Fashion': 0.48,
        'Lifestyle': 0.22,
        'Beauty': 0.13,
        'Travel': 0.10,
        'Home Decor': 0.07,
      },
    };
  }

  return null;
};

// Generate mock activity feed data
export const generateDemoActivities = (accountId: string, count: number = 20) => {
  const config = DEMO_ACCOUNTS[accountId as keyof typeof DEMO_ACCOUNTS];
  if (!config) return [];

  const activityTypes = [
    { type: 'like', weight: 0.3 },
    { type: 'identify_location', weight: 0.2 },
    { type: 'identify_interest', weight: 0.2 },
    { type: 'identify_language', weight: 0.15 },
    { type: 'profile_view', weight: 0.1 },
    { type: 'comment_like', weight: 0.05 },
  ];

  // Location data for @rhode (beauty brand - US-heavy)
  const locations = accountId === 'c8661641-a2e9-41d2-9c9e-2f63e781af56'
    ? ['United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France', 'Italy']
    : accountId === '320a4765-1df8-423f-925a-263538e2f059'
    ? ['Sweden', 'Norway', 'Denmark', 'United States', 'United Kingdom', 'Germany']
    : ['United States', 'United Kingdom', 'Canada', 'Brazil', 'Australia'];

  const interests = accountId === 'c8661641-a2e9-41d2-9c9e-2f63e781af56'
    ? ['Beauty', 'Skincare', 'Fashion', 'Lifestyle', 'Health & Wellness']
    : accountId === '7ed50c53-c969-4fa3-bd1d-c390c5f9a014'
    ? ['Lifestyle', 'Fashion', 'Coffee & Beverages', 'Beauty', 'Travel']
    : ['Fashion', 'Lifestyle', 'Beauty', 'Travel', 'Home & Decor'];

  const languages = ['English', 'Spanish', 'French', 'German', 'Portuguese', 'Italian'];

  // Sample target hashtags/accounts based on niche
  const targets = accountId === 'c8661641-a2e9-41d2-9c9e-2f63e781af56'
    ? [
        { type: 'hashtag', name: 'skincare' },
        { type: 'hashtag', name: 'beauty' },
        { type: 'hashtag', name: 'glowingskin' },
        { type: 'account', name: 'haileybieber' },
        { type: 'hashtag', name: 'skincareproducts' },
      ]
    : accountId === '7ed50c53-c969-4fa3-bd1d-c390c5f9a014'
    ? [
        { type: 'hashtag', name: 'lifestyle' },
        { type: 'hashtag', name: 'coffee' },
        { type: 'account', name: 'chamberlaincoffee' },
        { type: 'hashtag', name: 'vlogs' },
      ]
    : [
        { type: 'hashtag', name: 'fashion' },
        { type: 'hashtag', name: 'scandinavianstyle' },
        { type: 'account', name: 'djerfavenue' },
      ];

  // Sample profile pictures for thumbnails
  const sampleThumbnails = [
    'https://i.pravatar.cc/150?img=1',
    'https://i.pravatar.cc/150?img=5',
    'https://i.pravatar.cc/150?img=9',
  ];

  const activities = [];

  for (let i = 0; i < count; i++) {
    // Pick random activity type based on weights
    const random = Math.random();
    let cumulativeWeight = 0;
    let selectedType = activityTypes[0].type;

    for (const actType of activityTypes) {
      cumulativeWeight += actType.weight;
      if (random <= cumulativeWeight) {
        selectedType = actType.type;
        break;
      }
    }

    let subjectName = null;
    let selectedTarget = null;

    if (selectedType === 'identify_location') {
      subjectName = locations[Math.floor(Math.random() * locations.length)];
    } else if (selectedType === 'identify_interest') {
      subjectName = interests[Math.floor(Math.random() * interests.length)];
    } else if (selectedType === 'identify_language') {
      subjectName = languages[Math.floor(Math.random() * languages.length)];
    } else {
      selectedTarget = targets[Math.floor(Math.random() * targets.length)];
    }

    activities.push({
      id: `demo-activity-${accountId}-${i}`,
      account_id: accountId,
      type: selectedType,
      count: Math.floor(Math.random() * 15) + 3, // 3-17 users
      subject_name: subjectName,
      targets: selectedTarget,
      thumbnails: sampleThumbnails.slice(0, Math.floor(Math.random() * 3) + 1),
      created_at: subDays(new Date(), Math.random() * 2).toISOString(), // Last 2 days
    });
  }

  return activities.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
};
